import moment from 'moment-timezone';

import { IDatePickerStrings } from '@fluentui/react';

export const DatePickerStrings: IDatePickerStrings = {
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  goToToday: 'Go to today',
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  shortDays: ['U', 'M', 'T', 'W', 'R', 'F', 'S'],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

export function toDate(input: Date | string): Date {
  if (typeof input === 'string') {
    return new Date(input);
  } else {
    return input;
  }
}

function pad(num: number, size: number): string {
  let result = num + '';
  while (result.length < size) {
    result = '0' + result;
  }
  return result;
}

export function isChild(date: string): boolean {
  const patientAge = calculateAge(new Date(date));
  if (isNaN(patientAge) || patientAge >= 18) {
    return false;
  }
  return true;
}

export function calculateAge(birthday: Date | string): number {
  const date = toDate(birthday);
  const today = new Date();
  let _age = today.getFullYear() - date.getFullYear();
  const mos = today.getMonth() - date.getMonth();
  if (mos < 0 || (mos === 0 && today.getDate() < date.getDate())) {
    _age--;
  }
  return _age;
}

export function friendlyHour(date: Date): string {
  let hours = date.getHours();
  const suffix = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}${suffix}`;
}

export function friendlyFullDate(date: Date): string {
  const dayName = DatePickerStrings.days[date.getDay()];
  const month = DatePickerStrings.months[date.getMonth()];
  return `${dayName}, ${month} ${date.getDate()}, ${date.getFullYear()}`;
}

export function toShortDateString(value: Date): string {
  return `${value.getMonth() + 1}/${value.getDate()}/${value.getFullYear()}`;
}

export function toISODateString(input: Date | string): string {
  const value = toDate(input);
  return `${value.getFullYear()}-${pad(value.getMonth() + 1, 2)}-${pad(value.getDate(), 2)}`;
}

export function getZeroTimeDate(input?: Date | string) {
  let d = new Date();
  if (input) {
    d = toDate(input);
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

export function friendlyShortTime(date: Date): string {
  const hour = date.getHours();
  const minutes = date.getMinutes();

  let formattedShortTime = '';
  let newMinute = '';

  if (minutes < 10) {
    newMinute = '0' + minutes.toString();
  } else {
    newMinute = minutes.toString();
  }

  if (hour >= 12) {
    if (hour === 12) {
      formattedShortTime = hour.toString() + ':' + newMinute + ' PM';
    } else {
      formattedShortTime = (hour - 12).toString() + ':' + newMinute + ' PM';
    }
  } else {
    formattedShortTime = hour.toString() + ':' + newMinute + ' AM';
  }
  return formattedShortTime;
}

export function age(input: Date | string): number {
  const birthDate = toDate(input) || new Date(); // this lets us handle the case where input is note date-able.  age will be 0
  const today = new Date();
  const monthDelta = today.getMonth() - birthDate.getMonth();
  let years = today.getFullYear() - birthDate.getFullYear();

  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
    years--;
  }

  return years;
}

export function friendlyAge(input: Date | string): string {
  const now = moment();
  const dob = moment(input);

  const yr = now.diff(dob, 'years');
  const mo = now.diff(dob, 'months');
  const day = now.diff(dob, 'days');

  if (yr > 0) {
    return yr === 1 ? '1 yr' : `${yr} yrs`;
  } else if (mo > 0) {
    return mo === 1 ? '1 mo' : `${mo} mos`;
  } else if (day > 0) {
    return day === 1 ? '1 day' : `${day} days`;
  }

  return 'n/a';
}

export function daysFromNow(numDays: number): Date {
  return daysFromDate(new Date(), numDays);
}

export function daysFromDate(date: Date, numDays: number): Date {
  return moment(date).add(numDays, 'd').toDate();
}

export function isInRange(date: Date, start: Date, end: Date): boolean {
  return start <= date && date <= end;
}

export function isInPast(date: Date): boolean {
  return date < new Date();
}

export function formatDate(date?: Date): string {
  if (!date) {
    return '';
  }
  return date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear();
}
